import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { apiClient } from '../lib/api/client';
import { useAuth } from './AuthContext';

const CITY_EXEMPT_PATHS = ['/terms', '/privacy', '/refund-policy', '/contact'];

export interface CityOption {
  id: string;
  name: string;
  slug: string;
}

interface CityContextType {
  selectedCity: CityOption | null;
  setCity: (city: CityOption) => void;
  skipCitySelection: () => void;
  needsCitySelection: boolean;
  isDetectingCity: boolean;
}

const STORAGE_KEY = 'selectedCity';

const CityContext = createContext<CityContextType | undefined>(undefined);

interface CityProviderProps {
  children: ReactNode;
}

export function CityProvider({ children }: CityProviderProps) {
  const { user } = useAuth();
  const location = useLocation();
  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
  const [needsCitySelection, setNeedsCitySelection] = useState(false);
  const [isDetectingCity, setIsDetectingCity] = useState(false);

  const isExemptPath = CITY_EXEMPT_PATHS.includes(location.pathname);

  // Non-customer roles (vendors, admins) don't need city selection
  const pathname = window.location.pathname;
  const isNonCustomerRoute = pathname.startsWith('/vendor') || pathname.startsWith('/admin');
  const isNonCustomer = isNonCustomerRoute || (user !== null && user.role !== 'CUSTOMER');

  useEffect(() => {
    if (isNonCustomer) return;
    // 1. Check localStorage first
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as CityOption;
        if (parsed && parsed.id && parsed.name && parsed.slug) {
          setSelectedCity(parsed);
          return; // No detection needed
        }
      }
    } catch {
      // Ignore parse errors
    }

    // 2. Check if current URL path contains a citySlug (e.g. /:stateSlug/:citySlug/... or /:citySlug/:venueSlug/book)
    // URL patterns: /:stateSlug/:citySlug/:venueSlug and /:stateSlug/:citySlug/:venueSlug/book
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    // Venue/booking URLs have at least 3 path segments; citySlug is the 2nd segment
    if (pathParts.length >= 3) {
      const citySlug = pathParts[1];
      (async () => {
        try {
          const response = await apiClient.get<{ data: CityOption[] } | CityOption[]>(
            `/locations/cities`
          );
          const rawData = response.data;
          const cities: CityOption[] = Array.isArray(rawData)
            ? rawData
            : Array.isArray((rawData as { data: CityOption[] }).data)
              ? (rawData as { data: CityOption[] }).data
              : [];
          const matched = cities.find((c) => c.slug === citySlug);
          if (matched && matched.id) {
            const city = matched;
            setSelectedCity(city);
            try {
              localStorage.setItem(STORAGE_KEY, JSON.stringify(city));
            } catch {
              // Ignore storage errors
            }
            return;
          }
        } catch {
          // City slug not found — fall through to geolocation
        }
        // 3. Fall through to geolocation
        if (!navigator.geolocation) {
          setNeedsCitySelection(true);
          return;
        }
        setIsDetectingCity(true);
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              const { latitude, longitude } = position.coords;
              const geoResponse = await apiClient.get<CityOption>(
                `/locations/cities/nearest?lat=${latitude}&lng=${longitude}`
              );
              if (geoResponse.data && geoResponse.data.id) {
                const city = geoResponse.data;
                setSelectedCity(city);
                try {
                  localStorage.setItem(STORAGE_KEY, JSON.stringify(city));
                } catch {
                  // Ignore storage errors
                }
              } else {
                setNeedsCitySelection(true);
              }
            } catch (error) {
              console.error('Failed to detect city from location:', error);
              setNeedsCitySelection(true);
            } finally {
              setIsDetectingCity(false);
            }
          },
          () => {
            setIsDetectingCity(false);
            setNeedsCitySelection(true);
          },
          { timeout: 5000, maximumAge: 300000, enableHighAccuracy: false }
        );
      })();
      return;
    }

    // 3. No stored city — try geolocation
    if (!navigator.geolocation) {
      setNeedsCitySelection(true);
      return;
    }

    setIsDetectingCity(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await apiClient.get<CityOption>(
            `/locations/cities/nearest?lat=${latitude}&lng=${longitude}`
          );
          if (response.data && response.data.id) {
            const city = response.data;
            setSelectedCity(city);
            try {
              localStorage.setItem(STORAGE_KEY, JSON.stringify(city));
            } catch {
              // Ignore storage errors
            }
          } else {
            setNeedsCitySelection(true);
          }
        } catch (error) {
          console.error('Failed to detect city from location:', error);
          setNeedsCitySelection(true);
        } finally {
          setIsDetectingCity(false);
        }
      },
      () => {
        // Permission denied or error
        setIsDetectingCity(false);
        setNeedsCitySelection(true);
      },
      { timeout: 5000, maximumAge: 300000, enableHighAccuracy: false }
    );
  }, [isNonCustomer]);

  const setCity = (city: CityOption) => {
    setSelectedCity(city);
    setNeedsCitySelection(false);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(city));
    } catch {
      // Ignore storage errors
    }
  };

  const skipCitySelection = () => {
    setNeedsCitySelection(false);
  };

  return (
    <CityContext.Provider
      value={{
        selectedCity,
        setCity,
        skipCitySelection,
        needsCitySelection: needsCitySelection && !isNonCustomer && !isExemptPath,
        isDetectingCity,
      }}
    >
      {children}
    </CityContext.Provider>
  );
}

export function useCity(): CityContextType {
  const context = useContext(CityContext);
  if (context === undefined) {
    throw new Error('useCity must be used within a CityProvider');
  }
  return context;
}
